import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Button,
  Icon,
  Input,
  Loader,
  Message,
  Modal,
  Placeholder,
  Popup
} from 'semantic-ui-react';
import { IconFinger } from '../../../../../../components/IconFinger';
import {
  successNotification,
  warningNotification
} from '../../../../../../services/notification';
import {
  getPortoBiometryErrorMessage,
  isPortoSessionRenewing,
  PortoBiometryChannel,
  PortoBiometryResponse,
  PortoBiometrySendResult,
  usePortoBiometry,
  useSendPortoBiometry
} from '../../../../../../services/porto-biometry';
import {
  ChannelOption,
  ChannelOptions,
  ChannelPicker,
  ConfirmBox,
  FieldHint,
  FooterActions,
  Header,
  HeaderIcon,
  InfoList,
  LinkCard,
  LinkLabel,
  LinkRow,
  LinkValue,
  MetaList,
  Section,
  SendingBox,
  StatusCircle,
  Timeline,
  TimelineItem
} from './styles';
import {
  formatCountdown,
  formatDateTime,
  formatPhone,
  formatRecipient,
  getActionLabel,
  getChannelLabel,
  getRequesterLabel,
  isValidRecipient,
  onlyDigits
} from './utils';

type View = 'link' | 'confirm' | 'sending' | 'success';

export type PortoBiometryModalProps = {
  analysisId: string;
};

function useCountdown(target: string | null | undefined): number {
  const [now, setNow] = useState(Date.now());
  const targetMs = target ? new Date(target).getTime() : 0;
  const remaining = Math.max(targetMs - now, 0);

  useEffect(() => {
    if (!targetMs || targetMs <= Date.now()) {
      return;
    }
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [targetMs]);

  return remaining;
}

const CopyLinkButton = ({ url }: { url: string }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        successNotification('Sucesso!', 'Link copiado para a área de transferência!');
      })
      .catch(() => warningNotification('Ops...', 'Erro ao copiar o link, tente novamente'));
  };

  return (
    <Button
      type="button"
      size="small"
      color={copied ? 'green' : undefined}
      basic={!copied}
      icon
      labelPosition="left"
      onClick={handleCopy}
    >
      <Icon name={copied ? 'check' : 'copy outline'} />
      {copied ? 'Copiado' : 'Copiar'}
    </Button>
  );
};

const LinkBox = ({
  url,
  label,
  highlight
}: {
  url: string;
  label: string;
  highlight?: boolean;
}) => (
  <LinkCard highlight={highlight}>
    <LinkLabel>{label}</LinkLabel>
    <LinkRow>
      <LinkValue href={url} target="_blank" rel="noopener noreferrer" title={url}>
        {url}
      </LinkValue>
      <CopyLinkButton url={url} />
      <Button
        as="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        basic
        icon
        labelPosition="left"
      >
        <Icon name="external alternate" />
        Abrir
      </Button>
    </LinkRow>
  </LinkCard>
);

const LoadingState = () => (
  <Placeholder fluid>
    <Placeholder.Paragraph>
      <Placeholder.Line length="short" />
      <Placeholder.Line length="full" />
      <Placeholder.Line length="medium" />
      <Placeholder.Line length="long" />
    </Placeholder.Paragraph>
  </Placeholder>
);

const HistorySection = ({ data }: { data: PortoBiometryResponse }) => {
  const [open, setOpen] = useState(false);
  if (!data.history.length) {
    return null;
  }

  return (
    <Section>
      <Accordion>
        <Accordion.Title active={open} onClick={() => setOpen(!open)}>
          <Icon name="dropdown" />
          Histórico de envios ({data.history.length})
        </Accordion.Title>
        <Accordion.Content active={open}>
          <Timeline>
            {data.history.map((item, index) => {
              const requester = getRequesterLabel(item.requester);
              const when = formatDateTime(item.date) || item.rawDate;
              return (
                <TimelineItem key={`${item.action}-${item.rawDate}-${index}`} processing={item.processing}>
                  <strong>{getActionLabel(item.action)}</strong>
                  <span>
                    {[when, requester && `por ${requester}`].filter(Boolean).join(' · ')}
                  </span>
                  {item.deliveries.map(delivery => (
                    <span key={`${delivery.channel}-${delivery.recipient}`}>
                      Via {getChannelLabel(delivery.channel)}:{' '}
                      {formatRecipient(delivery.channel, delivery.recipient)}
                    </span>
                  ))}
                  {item.processing && item.processing !== 'Sucesso' && item.message && (
                    <span>Retorno da Porto: {item.message}</span>
                  )}
                </TimelineItem>
              );
            })}
          </Timeline>
        </Accordion.Content>
      </Accordion>
    </Section>
  );
};

const PortoBiometryModal = ({ analysisId }: PortoBiometryModalProps) => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('link');
  const [channel, setChannel] = useState<PortoBiometryChannel>('SMS');
  const [recipient, setRecipient] = useState('');
  const [touched, setTouched] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<PortoBiometrySendResult | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = usePortoBiometry(
    analysisId,
    open
  );
  const { mutate: sendBiometry, isLoading: isSending } = useSendPortoBiometry(analysisId);

  const isRenewing = isError && isPortoSessionRenewing(error);
  const cooldownRemaining = useCountdown(cooldownUntil || data?.nextSendAvailableAt);
  const recipientIsValid = isValidRecipient(channel, recipient);

  const suggestedRecipient = useMemo(() => {
    if (!data) {
      return '';
    }
    return channel === 'SMS'
      ? formatPhone(data.suggestedRecipients.sms)
      : data.suggestedRecipients.email || '';
  }, [channel, data]);

  const handleOpen = () => {
    setView('link');
    setOpen(true);
  };

  const handleClose = () => {
    if (isSending) {
      return;
    }
    setOpen(false);
    setSendResult(null);
    setSendError(null);
  };

  const startConfirm = () => {
    const lastChannel = data?.lastSend?.channel === 'EMAIL' ? 'EMAIL' : 'SMS';
    setChannel(lastChannel);
    setRecipient(
      lastChannel === 'SMS'
        ? formatPhone(data?.suggestedRecipients.sms)
        : data?.suggestedRecipients.email || ''
    );
    setTouched(false);
    setSendError(null);
    setView('confirm');
  };

  const changeChannel = (next: PortoBiometryChannel) => {
    if (next === channel) {
      return;
    }
    setChannel(next);
    setRecipient(
      next === 'SMS'
        ? formatPhone(data?.suggestedRecipients.sms)
        : data?.suggestedRecipients.email || ''
    );
    setTouched(false);
  };

  const handleSend = () => {
    setTouched(true);
    if (!recipientIsValid) {
      return;
    }
    setSendError(null);
    setView('sending');
    sendBiometry(
      {
        channel,
        recipient: channel === 'SMS' ? onlyDigits(recipient) : recipient.trim()
      },
      {
        onSuccess: result => {
          setSendResult(result);
          setCooldownUntil(result.nextSendAvailableAt);
          setView('success');
        },
        onError: err => {
          if (err?.nextSendAvailableAt) {
            setCooldownUntil(err.nextSendAvailableAt);
          }
          setSendError(getPortoBiometryErrorMessage(err));
          setView('confirm');
        }
      }
    );
  };

  const renderLinkView = () => {
    if (isLoading) {
      return <LoadingState />;
    }

    if (isRenewing) {
      return (
        <Message info icon>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Conectando ao portal da Porto…</Message.Header>
            Estamos renovando o acesso ao portal da Porto. Isso pode levar até 2 minutos; o link
            aparece aqui automaticamente.
          </Message.Content>
        </Message>
      );
    }

    if (isError || !data) {
      return (
        <Message warning icon>
          <Icon name="plug" />
          <Message.Content>
            <Message.Header>Não conseguimos consultar a Porto agora</Message.Header>
            {getPortoBiometryErrorMessage(error)}
          </Message.Content>
        </Message>
      );
    }

    return (
      <>
        {data.url ? (
          <LinkBox url={data.url} label="Link de biometria" />
        ) : (
          <Message info icon>
            <Icon name="hourglass half" />
            <Message.Content>
              <Message.Header>Link ainda não disponível</Message.Header>
              A Porto ainda não gerou o link de biometria deste orçamento. Isso costuma levar
              alguns minutos após a análise entrar em biometria.
            </Message.Content>
          </Message>
        )}

        {(data.generatedAt || data.lastSend) && (
          <MetaList>
            {data.generatedAt && (
              <li>
                <Icon name="calendar outline" />
                Link gerado em {formatDateTime(data.generatedAt)}
              </li>
            )}
            {data.lastSend && (
              <li>
                <Icon name={data.lastSend.channel === 'EMAIL' ? 'mail outline' : 'mobile alternate'} />
                Último envio por {getChannelLabel(data.lastSend.channel)} para{' '}
                <b>{formatRecipient(data.lastSend.channel, data.lastSend.recipient)}</b>
                {data.lastSend.sentAt && ` em ${formatDateTime(data.lastSend.sentAt)}`}
              </li>
            )}
          </MetaList>
        )}

        <HistorySection data={data} />
      </>
    );
  };

  const renderConfirmView = () => (
    <ConfirmBox>
      <StatusCircle tone="warning">
        <Icon name="redo" size="large" />
      </StatusCircle>
      <h4>Solicitar nova biometria?</h4>
      <p>
        A Porto vai enviar um <b>link atualizado</b> para o locatário concluir a biometria
        facial. O status da análise continua em <b>BIOMETRIA</b> até a conclusão.
      </p>

      <InfoList>
        <li>
          <Icon name="info circle" />
          Use quando o locatário não recebeu, perdeu o link ou não conseguiu concluir a biometria.
        </li>
        <li>
          <Icon name="check circle outline" />
          Confira o contato abaixo: é para ele que a Porto vai enviar o link.
        </li>
      </InfoList>

      <ChannelPicker>
        <label>Enviar por</label>
        <ChannelOptions role="radiogroup" aria-label="Canal de envio">
          <ChannelOption
            type="button"
            role="radio"
            aria-checked={channel === 'SMS'}
            active={channel === 'SMS'}
            onClick={() => changeChannel('SMS')}
          >
            <Icon name="mobile alternate" /> SMS
          </ChannelOption>
          <ChannelOption
            type="button"
            role="radio"
            aria-checked={channel === 'EMAIL'}
            active={channel === 'EMAIL'}
            onClick={() => changeChannel('EMAIL')}
          >
            <Icon name="mail outline" /> E-mail
          </ChannelOption>
        </ChannelOptions>

        <Input
          fluid
          icon={channel === 'SMS' ? 'phone' : 'at'}
          iconPosition="left"
          type={channel === 'SMS' ? 'tel' : 'email'}
          placeholder={channel === 'SMS' ? '(00) 90000-0000' : 'locatario@email.com'}
          value={recipient}
          error={touched && !recipientIsValid}
          onChange={(_, { value }) =>
            setRecipient(channel === 'SMS' ? formatPhone(value) : value)
          }
          onBlur={() => setTouched(true)}
        />
        {touched && !recipientIsValid ? (
          <FieldHint error>
            {channel === 'SMS'
              ? 'Informe um celular válido com DDD.'
              : 'Informe um e-mail válido.'}
          </FieldHint>
        ) : (
          suggestedRecipient &&
          recipient !== suggestedRecipient && (
            <FieldHint>
              Contato cadastrado: {suggestedRecipient}{' '}
              <a
                href="#"
                onClick={event => {
                  event.preventDefault();
                  setRecipient(suggestedRecipient);
                }}
              >
                usar este
              </a>
            </FieldHint>
          )
        )}
      </ChannelPicker>

      {sendError && (
        <Message negative size="small" style={{ textAlign: 'left' }}>
          {sendError}
        </Message>
      )}
    </ConfirmBox>
  );

  const renderSendingView = () => (
    <SendingBox>
      <Loader active inline="centered" size="large" />
      <h4>Solicitando à Porto o envio do link…</h4>
      <p>Isso pode levar alguns segundos. Não feche esta janela.</p>
    </SendingBox>
  );

  const renderSuccessView = () => {
    if (!sendResult) {
      return null;
    }
    const { sent } = sendResult;
    return (
      <>
        <ConfirmBox>
          <StatusCircle tone="success">
            <Icon name="check" size="large" />
          </StatusCircle>
          <h4>Link enviado!</h4>
          <p>
            A Porto enviou o link atualizado por {getChannelLabel(sent.channel)} para{' '}
            <b>{formatRecipient(sent.channel, sent.recipient)}</b>. Você também pode copiar e
            compartilhar o link abaixo.
          </p>
        </ConfirmBox>
        {sendResult.url && (
          <Section>
            <LinkBox url={sendResult.url} label="Link atualizado" highlight />
          </Section>
        )}
      </>
    );
  };

  const renderActions = () => {
    if (view === 'confirm') {
      return (
        <FooterActions>
          <Button type="button" basic onClick={() => setView('link')}>
            Voltar
          </Button>
          <Button
            type="button"
            color="orange"
            icon
            labelPosition="right"
            disabled={cooldownRemaining > 0}
            onClick={handleSend}
          >
            {cooldownRemaining > 0
              ? `Disponível em ${formatCountdown(cooldownRemaining)}`
              : 'Enviar link'}
            <Icon name="send" />
          </Button>
        </FooterActions>
      );
    }

    if (view === 'sending') {
      return (
        <FooterActions>
          <span />
          <Button type="button" disabled loading>
            Enviando
          </Button>
        </FooterActions>
      );
    }

    if (view === 'success') {
      return (
        <FooterActions>
          <Button type="button" basic onClick={() => setView('link')}>
            Ver histórico
          </Button>
          <Button type="button" primary onClick={handleClose}>
            Concluir
          </Button>
        </FooterActions>
      );
    }

    const canRequest = Boolean(data?.url) && !isLoading;
    return (
      <FooterActions>
        {isRenewing ? (
          <span />
        ) : isError ? (
          <Button
            type="button"
            basic
            icon
            labelPosition="left"
            loading={isFetching}
            onClick={() => refetch()}
          >
            <Icon name="refresh" />
            Tentar novamente
          </Button>
        ) : !data?.url && !isLoading ? (
          <Button
            type="button"
            basic
            icon
            labelPosition="left"
            loading={isFetching}
            onClick={() => refetch()}
          >
            <Icon name="refresh" />
            Atualizar
          </Button>
        ) : (
          <Popup
            disabled={cooldownRemaining <= 0}
            content="Um link acabou de ser enviado. Aguarde para solicitar novamente."
            trigger={
              <span>
                <Button
                  type="button"
                  basic
                  color="orange"
                  icon
                  labelPosition="left"
                  disabled={!canRequest || cooldownRemaining > 0}
                  onClick={startConfirm}
                >
                  <Icon name="redo" />
                  {cooldownRemaining > 0
                    ? `Nova solicitação em ${formatCountdown(cooldownRemaining)}`
                    : 'Solicitar nova biometria'}
                </Button>
              </span>
            }
          />
        )}
        <Button type="button" primary onClick={handleClose}>
          Fechar
        </Button>
      </FooterActions>
    );
  };

  return (
    <div style={{ zIndex: 10, position: 'relative' }}>
      <Modal
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }}
        size="small"
        closeIcon={!isSending}
        closeOnDimmerClick={!isSending}
        open={open}
        onClose={handleClose}
        trigger={
          <Popup
            content="Ver link de biometria"
            trigger={
              <Button color="orange" icon onClick={handleOpen}>
                <IconFinger size={18} color="#FFFFFF" />
              </Button>
            }
          />
        }
      >
        <Modal.Content>
          {view === 'link' && (
            <Header>
              <HeaderIcon>
                <IconFinger size={20} color="#F2711C" />
              </HeaderIcon>
              <div>
                <h4>Biometria Porto Seguro</h4>
                <p>
                  Compartilhe o link com o locatário. Após a biometria, o status muda para{' '}
                  <b>APROVADO</b> e o seguro poderá ser contratado.
                </p>
              </div>
            </Header>
          )}
          {view === 'link' && renderLinkView()}
          {view === 'confirm' && renderConfirmView()}
          {view === 'sending' && renderSendingView()}
          {view === 'success' && renderSuccessView()}
        </Modal.Content>
        <Modal.Actions>{renderActions()}</Modal.Actions>
      </Modal>
    </div>
  );
};

export default PortoBiometryModal;
